import { useMemo, useState, Fragment } from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import LayersIcon from '@mui/icons-material/LayersOutlined';
import AutoStoriesIcon from '@mui/icons-material/AutoStoriesOutlined';
import BugReportIcon from '@mui/icons-material/BugReportOutlined';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import { useFilteredRows } from '../hooks/useFilteredRows';
import { useModel } from '../hooks/useModel';
import { fmtInt, num, secondsToDays } from '../lib/format';
import type { Epic, Child } from '../lib/model';
import { StatusChip } from './StatusChip';
import { useT } from '../i18n';

type SortKey = 'plan' | 'actual' | 'kids' | 'key';
type SortDir = 'asc' | 'desc';

function typeIcon(t: string | undefined) {
  if (t === 'Epic') return <LayersIcon fontSize="small" sx={{ color: '#6b5970' }} />;
  if (t === 'Story') return <AutoStoriesIcon fontSize="small" sx={{ color: '#2e7d32' }} />;
  if (t === 'Bug') return <BugReportIcon fontSize="small" sx={{ color: '#c62828' }} />;
  return null;
}

export function EpicsTable() {
  const t = useT();
  const model = useModel();
  const visibleRows = useFilteredRows();
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [sortKey, setSortKey] = useState<SortKey>('plan');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  // Welche Epics nach Filter überhaupt sichtbar? Ein Epic ist sichtbar, wenn:
  //   - Das Epic selbst durch den Filter geht ODER
  //   - mindestens ein Kid durch den Filter geht
  // Das matched die V1-Logik (Filter-aware Epic-Liste).
  const visibleKeys = useMemo(() => new Set(visibleRows.map(r => r['Issue key'])), [visibleRows]);

  const filteredEpics = useMemo(() => {
    return model.epics.filter(e => {
      // Real-Epics: zeigen wenn entweder die Epic-Zeile oder eines ihrer Kids matched
      if (visibleKeys.has(e['Issue key'])) return true;
      return (e._kids || []).some(k => visibleKeys.has(k['Issue key']));
    });
  }, [model.epics, visibleKeys]);

  const sortedEpics = useMemo(() => {
    const arr = [...filteredEpics];
    arr.sort((a, b) => {
      // Pseudo-Epic für Orphans immer ans Ende
      if (a._isOrphanContainer && !b._isOrphanContainer) return 1;
      if (!a._isOrphanContainer && b._isOrphanContainer) return -1;
      let va: number | string; let vb: number | string;
      switch (sortKey) {
        case 'plan':   va = a._totalPlan;   vb = b._totalPlan;   break;
        case 'actual': va = a._totalActual; vb = b._totalActual; break;
        case 'kids':   va = a._kidCount;    vb = b._kidCount;    break;
        case 'key':    va = a['Issue key'] ?? ''; vb = b['Issue key'] ?? ''; break;
      }
      const cmp = va < vb ? -1 : va > vb ? 1 : 0;
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return arr;
  }, [filteredEpics, sortKey, sortDir]);

  function toggle(key: string) {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function expandAll() {
    setExpanded(new Set(sortedEpics.map(e => e['Issue key'])));
  }
  function collapseAll() {
    setExpanded(new Set());
  }

  function setSort(key: SortKey) {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('desc'); }
  }

  if (!visibleRows.length) return null;

  const totalChildren = filteredEpics.reduce((s, e) => s + (e._kidCount || 0), 0);
  const tt = t;

  return (
    <Card>
      <CardContent>
        <Stack direction="row" alignItems="center" sx={{ mb: 1.5 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="overline" color="text.secondary">{tt.epicsTable.title}</Typography>
            <Typography variant="body2" color="text.secondary">
              {fmtInt(filteredEpics.length)} · {fmtInt(totalChildren)} {tt.epicsTable.colKids}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Button size="small" startIcon={<UnfoldMoreIcon />} onClick={expandAll}>{tt.epicsTable.expandAll}</Button>
            <Button size="small" startIcon={<UnfoldLessIcon />} onClick={collapseAll}>{tt.epicsTable.collapseAll}</Button>
          </Stack>
        </Stack>

        <TableContainer sx={{ borderRadius: 2, border: 1, borderColor: 'divider', maxHeight: '70vh' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 32 }} />
                <TableCell sx={{ width: 36 }}>{tt.epicsTable.colType}</TableCell>
                <TableCell
                  sx={{ width: 130, cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => setSort('key')}
                >
                  {tt.epicsTable.colKey} {sortKey === 'key' && (sortDir === 'asc' ? '↑' : '↓')}
                </TableCell>
                <TableCell>{tt.epicsTable.colSummary}</TableCell>
                <TableCell sx={{ width: 130 }}>{tt.epicsTable.colStatus}</TableCell>
                <TableCell sx={{ width: 120 }}>{tt.epicsTable.colAssignee}</TableCell>
                <TableCell sx={{ width: 110 }}>{tt.epicsTable.colTeam}</TableCell>
                <TableCell sx={{ width: 90 }}>{tt.epicsTable.priority}</TableCell>
                <TableCell align="right" sx={{ width: 80 }}>{tt.epicsTable.colRough}</TableCell>
                <TableCell align="right" sx={{ width: 70 }}>{tt.epicsTable.colSp}</TableCell>
                <TableCell align="right" sx={{ width: 90 }}>{tt.epicsTable.colEstimate}</TableCell>
                <TableCell
                  align="right"
                  sx={{ width: 90, cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => setSort('plan')}
                >
                  {tt.epicsTable.colPlan} {sortKey === 'plan' && (sortDir === 'asc' ? '↑' : '↓')}
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ width: 90, cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => setSort('actual')}
                >
                  {tt.epicsTable.colActual} {sortKey === 'actual' && (sortDir === 'asc' ? '↑' : '↓')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedEpics.map(epic => (
                <EpicRows
                  key={epic['Issue key']}
                  epic={epic}
                  isOpen={expanded.has(epic['Issue key'])}
                  onToggle={() => toggle(epic['Issue key'])}
                  visibleKeys={visibleKeys}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
}

function EpicRows({ epic, isOpen, onToggle, visibleKeys }: {
  epic: Epic;
  isOpen: boolean;
  onToggle: () => void;
  visibleKeys: Set<string>;
}) {
  const isOrphan = epic._isOrphanContainer === true;
  const kids = (epic._kids ?? []).filter(k => visibleKeys.has(k['Issue key']));
  const orphanSx = isOrphan ? { borderLeft: '3px solid', borderLeftColor: 'warning.main' } : {};

  return (
    <Fragment>
      <TableRow
        hover
        sx={{
          ...orphanSx,
          bgcolor: 'action.hover',
          '& > td': { fontWeight: 500 },
        }}
        onClick={onToggle}
        style={{ cursor: 'pointer' }}
      >
        <TableCell>
          <IconButton size="small">
            {isOpen ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
          </IconButton>
        </TableCell>
        <TableCell>{typeIcon('Epic')}</TableCell>
        <TableCell>
          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12 }}>
            {isOrphan ? '—' : epic['Issue key']}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2" noWrap title={epic['Summary']}>
            {epic['Summary']}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {epic._kidCount} Child items · {epic._refinedCount} refined · {epic._doneCount} done
          </Typography>
        </TableCell>
        <TableCell><StatusChip status={epic['Status']} /></TableCell>
        <TableCell><Typography variant="body2" noWrap>{epic['Assignee']}</Typography></TableCell>
        <TableCell>
          {epic['Team Name'] ? <Chip label={epic['Team Name']} size="small" variant="outlined" /> : null}
        </TableCell>
        <TableCell><Typography variant="body2">{epic['Priority']}</Typography></TableCell>
        <TableCell align="right">{fmtInt(epic._epicRough)}</TableCell>
        <TableCell align="right">–</TableCell>
        <TableCell align="right">–</TableCell>
        <TableCell align="right">
          <Tooltip
            arrow
            title={`Refined Kids: ${fmtInt(epic._refinedKidPlan)} PT · Rest aus Rough: ${fmtInt(epic._epicRest)} PT`}
          >
            <Box sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-end' }}>
              <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                {fmtInt(epic._totalPlan)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Rest: {fmtInt(epic._epicRest)}
              </Typography>
            </Box>
          </Tooltip>
        </TableCell>
        <TableCell align="right">
          <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
            {fmtInt(epic._totalActual)}
          </Typography>
        </TableCell>
      </TableRow>

      {isOpen && kids.length === 0 && (
        <TableRow>
          <TableCell />
          <TableCell colSpan={12} sx={{ color: 'text.secondary', fontStyle: 'italic', pl: 6 }}>
            Noch keine Stories/Bugs (oder durch Filter ausgeblendet).
          </TableCell>
        </TableRow>
      )}

      {isOpen && kids.map(child => (
        <ChildRow key={child['Issue key']} child={child} />
      ))}
    </Fragment>
  );
}

function ChildRow({ child }: { child: Child }) {
  const sp  = num(child['Custom field (Story Points)']);
  const est = secondsToDays(child['Σ Original Estimate'] ?? child['Original Estimate']);
  const rh  = num(child['Custom field (Rough Estimation (d))']);
  return (
    <TableRow hover>
      <TableCell />
      <TableCell>{typeIcon(child['Issue Type'] as string)}</TableCell>
      <TableCell>
        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12 }}>
          {child['Issue key']}
        </Typography>
      </TableCell>
      <TableCell sx={{ pl: 4 }}>
        <Typography variant="body2" noWrap title={child['Summary']}>{child['Summary']}</Typography>
      </TableCell>
      <TableCell><StatusChip status={child['Status']} /></TableCell>
      <TableCell><Typography variant="body2" noWrap>{child['Assignee']}</Typography></TableCell>
      <TableCell>
        {child['Team Name'] ? <Chip label={child['Team Name']} size="small" variant="outlined" /> : null}
      </TableCell>
      <TableCell><Typography variant="body2">{child['Priority']}</Typography></TableCell>
      <TableCell align="right">{rh != null ? fmtInt(rh) : ''}</TableCell>
      <TableCell align="right">{sp != null ? fmtInt(sp) : ''}</TableCell>
      <TableCell align="right">{est != null ? fmtInt(est) : ''}</TableCell>
      <TableCell align="right">
        <Tooltip arrow title={`${child._plan.label} (${child._plan.src})`}>
          <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
            {fmtInt(child._plan.val)}
          </Typography>
        </Tooltip>
      </TableCell>
      <TableCell align="right">
        <Tooltip arrow title={`${child._actual.label} (${child._actual.src})`}>
          <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
            {fmtInt(child._actual.val)}
          </Typography>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
}

